import styled, { css, keyframes } from 'styled-components';
import {
  STEP_LIST_DENSITY_TOKENS,
  STEP_STATUS_CANCELLED,
  STEP_STATUS_FAILED,
  STEP_STATUS_IN_PROGRESS,
  STEP_STATUS_PENDING,
  STEP_STATUS_SKIPPED,
  STEP_STATUS_SUCCEEDED,
  type StepListDensity,
  type StepStatus,
} from './StepList.constants';

const STATUS_COLOR: Record<
  StepStatus,
  (theme: { colors?: Record<string, string | undefined> }) => {
    fg: string;
    bg: string;
    line: string;
  }
> = {
  pending: ({ colors = {} }) => ({
    fg: colors.onSurfaceVariant ?? '#C7C4D7',
    bg: 'transparent',
    line: colors.outlineVariant ?? '#464555',
  }),
  inProgress: ({ colors = {} }) => {
    const fg = colors.primary ?? '#C1C1FF';
    return {
      fg,
      bg: `color-mix(in oklch, ${fg} 18%, transparent)`,
      line: colors.outlineVariant ?? '#464555',
    };
  },
  succeeded: ({ colors = {} }) => {
    const fg = colors.success ?? '#8CD88C';
    return {
      fg,
      bg: `color-mix(in oklch, ${fg} 22%, transparent)`,
      line: `color-mix(in oklch, ${fg} 50%, transparent)`,
    };
  },
  failed: ({ colors = {} }) => {
    const fg = colors.error ?? '#FFB4AB';
    return {
      fg,
      bg: `color-mix(in oklch, ${fg} 22%, transparent)`,
      line: `color-mix(in oklch, ${fg} 50%, transparent)`,
    };
  },
  skipped: ({ colors = {} }) => ({
    fg: colors.onSurfaceVariant ?? '#C7C4D7',
    bg: 'transparent',
    line: colors.outlineVariant ?? '#464555',
  }),
  cancelled: ({ colors = {} }) => {
    const fg = colors.caution ?? '#FFDF9E';
    return {
      fg,
      bg: `color-mix(in oklch, ${fg} 18%, transparent)`,
      line: `color-mix(in oklch, ${fg} 40%, transparent)`,
    };
  },
};

export const Root = styled.ol`
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  list-style: none;

  &[data-variant='dot'] {
    padding: 4px 14px 14px;
    gap: 12px;
  }
`;

interface ItemProps {
  $density: StepListDensity;
}

export const Item = styled.li<ItemProps>`
  display: flex;
  align-items: stretch;
  ${({ $density }) => {
    const tokens = STEP_LIST_DENSITY_TOKENS[$density];
    return css`
      gap: ${tokens.rowGap};
    `;
  }}
`;

interface RailProps {
  $density: StepListDensity;
}

export const Rail = styled.div<RailProps>`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
  ${({ $density }) => css`
    width: ${STEP_LIST_DENSITY_TOKENS[$density].indicatorSize};
  `}
`;

interface IndicatorProps {
  $status: StepStatus;
  $density: StepListDensity;
}

export const Indicator = styled.span<IndicatorProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border-radius: 50%;
  border: 1.5px solid;
  ${({ $density }) => {
    const tokens = STEP_LIST_DENSITY_TOKENS[$density];
    return css`
      width: ${tokens.indicatorSize};
      height: ${tokens.indicatorSize};
    `;
  }}

  ${({ $status, theme }) => {
    const palette =
      STATUS_COLOR[$status]?.(theme) ?? STATUS_COLOR.pending(theme);
    return css`
      color: ${palette.fg};
      border-color: ${palette.fg};
      background: ${palette.bg};
    `;
  }}

  /* Filled variants for completed states get a solid disc instead of a
     ring — better at-a-glance state read down a long step list. */
  ${({ $status, theme }) => {
    const isFilled =
      $status === STEP_STATUS_SUCCEEDED ||
      $status === STEP_STATUS_FAILED ||
      $status === STEP_STATUS_IN_PROGRESS;
    if (!isFilled) return '';
    const palette = STATUS_COLOR[$status](theme);
    // `theme` is already in scope — read it directly instead of nesting
    // another `({ theme }) => …` closure inside the returned css block.
    return css`
      background: ${palette.fg};
      color: ${theme.colors?.onPrimary ?? '#13131B'};
    `;
  }}

  ${({ $status }) =>
    $status === STEP_STATUS_SKIPPED &&
    css`
      opacity: 0.6;
    `}

  & > svg {
    width: 60%;
    height: 60%;
  }
`;

interface ConnectorProps {
  $status: StepStatus;
  $density: StepListDensity;
}

/**
 * The vertical line connecting one step's indicator to the next. Drawn
 * inside the rail so it sits dead-centre under the indicator regardless
 * of indicator size. The line's tone follows the *upper* step's status
 * (matches user mental model: "this step finished, so the line below it
 * is green").
 */
export const Connector = styled.div<ConnectorProps>`
  flex: 1 1 auto;
  width: 2px;
  margin: 2px 0;
  border-radius: 1px;
  ${({ $status, theme }) => {
    const palette =
      STATUS_COLOR[$status]?.(theme) ?? STATUS_COLOR.pending(theme);
    return css`
      background: ${palette.line};
    `;
  }}
`;

interface BodyProps {
  $density: StepListDensity;
  $isLast: boolean;
  $dot?: boolean;
}

export const Body = styled.div<BodyProps>`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-width: 0;
  gap: 2px;
  ${({ $density, $isLast, $dot }) => {
    if ($dot) return '';
    const tokens = STEP_LIST_DENSITY_TOKENS[$density];
    return css`
      padding-top: ${tokens.rowPadY};
      padding-bottom: ${$isLast ? tokens.rowPadY : `calc(${tokens.rowPadY} + 8px)`};
    `;
  }}
`;

export const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-width: 0;
`;

export const Title = styled.span<{ $density: StepListDensity; $skipped: boolean }>`
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  ${({ $density }) => css`
    font-size: ${STEP_LIST_DENSITY_TOKENS[$density].titleFontSize};
  `}
  ${({ theme: { colors = {} } }) => css`
    color: ${colors.onSurface ?? '#E4E1ED'};
  `}
  ${({ $skipped }) =>
    $skipped &&
    css`
      text-decoration: line-through;
      opacity: 0.7;
    `}
`;

export const Meta = styled.span<{ $density: StepListDensity }>`
  flex-shrink: 0;
  font-variant-numeric: tabular-nums;
  ${({ $density }) => css`
    font-size: ${STEP_LIST_DENSITY_TOKENS[$density].metaFontSize};
  `}
  ${({ theme: { colors = {} } }) => css`
    color: ${colors.onSurfaceVariant ?? '#C7C4D7'};
  `}
`;

export const Description = styled.div<{ $density: StepListDensity }>`
  ${({ $density }) => css`
    font-size: ${STEP_LIST_DENSITY_TOKENS[$density].descriptionFontSize};
  `}
  ${({ theme: { colors = {} } }) => css`
    color: ${colors.onSurfaceVariant ?? '#C7C4D7'};
  `}
  line-height: 1.45;
`;

/* ── dot variant ───────────────────────────────────────────────────── */

const dotSpin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

export const DotItem = styled.li`
  display: grid;
  grid-template-columns: 22px 1fr;
  gap: 10px;
  align-items: start;
`;

export const DotMarker = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  margin-top: 2px;
`;

const DOT_COLOR: Record<
  string,
  (colors: Record<string, string | undefined>) => string
> = {
  [STEP_STATUS_SUCCEEDED]: (c) => c.successFixed ?? '#4ADE80',
  [STEP_STATUS_FAILED]: (c) => c.errorFixed ?? '#F87171',
  [STEP_STATUS_IN_PROGRESS]: (c) => c.primary ?? '#C1C1FF',
  [STEP_STATUS_PENDING]: (c) => c.outlineVariant ?? '#464555',
  [STEP_STATUS_SKIPPED]: (c) => c.outlineVariant ?? '#464555',
  [STEP_STATUS_CANCELLED]: (c) => c.warningFixed ?? '#FBBF24',
};

export const DotIndicator = styled.span<{ $status: StepStatus }>`
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: ${({ $status, theme: { colors = {} } }) =>
    (DOT_COLOR[$status] ?? DOT_COLOR[STEP_STATUS_PENDING])(colors)};
`;

export const DotSpinnerIcon = styled.span`
  font-size: 18px;
  color: ${({ theme: { colors = {} } }) => colors.primary ?? '#C1C1FF'};
  animation: ${dotSpin} 900ms linear infinite;
`;
